import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import {
  useCreateTerminalProfile,
  useTerminalProfiles,
  type TerminalProfile,
} from '@/hooks/use-terminals';
import { ProfileConfigEditor } from './profile-config-editor';
import { ProfilePeripheralEditor } from './profile-peripheral-editor';
import { ParameterEditor } from '@/pages/parameters/parameter-editor';
import { PinnedParametersToggle } from '@/pages/parameters/pinned-parameters';
import {
  useBindProfileCountryPack,
  useCountryPacks,
  useUnbindProfileCountryPack,
  type CountryPack,
} from '@/hooks/use-country-packs';
import { useT } from '@/i18n/use-t';

/** The shape `useT` returns, for the helper below that cannot call a hook. */
type Translate = (key: string, bakedEnglish?: string) => string;

const Schema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(1024).optional(),
  primaryLocale: z.string().regex(/^[a-z]{2,3}$/, 'lowercase ISO-639'),
  availableLocalesCsv: z
    .string()
    .min(2)
    .regex(/^[a-z]{2,3}(\s*,\s*[a-z]{2,3})*$/, 'comma-separated lowercase ISO-639 codes'),
  // RFL-177 — terminals on this profile have a produce scale; grants the
  // weight-standards sync domain beyond the SCO kind default.
  hasScale: z.boolean(),
  // ADR-0012 §3 / FN-96 — which executable the profile's terminals run.
  applicationVariant: z.enum(['', 'food_self_order', 'kitchen_station']),
});
type FormValues = z.infer<typeof Schema>;

export function TerminalProfilesPage() {
  const t = useT('admin_ui');
  const profiles = useTerminalProfiles();
  const create = useCreateTerminalProfile();
  const enabledPacks = useCountryPacks({ enabled: true });
  const allPacks = useCountryPacks();
  const [error, setError] = useState<string | null>(null);
  // RL-279 — the parameter editor pinned to one profile, opened from its row
  // and rendered in a full-width row beneath it (TP-FR-009).
  const [parametersOpenFor, setParametersOpenFor] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: {
      name: '',
      description: '',
      primaryLocale: 'en',
      availableLocalesCsv: 'en',
      hasScale: false,
      applicationVariant: '',
    },
  });

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">
          {t('terminals.profiles.title', 'Terminal profiles')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {profiles.data === undefined
            ? '…'
            : t(
                'terminals.profiles.count',
                '{count} profile(s). Each terminal can be bound to one profile.',
              ).replace('{count}', String(profiles.data.length))}
        </p>
      </header>

      <section className="rounded-md border bg-card p-5">
        <h2 className="mb-3 text-sm font-medium">{t('terminals.profiles.new', 'New profile')}</h2>
        <form
          className="grid gap-3 md:grid-cols-[2fr_2fr_1fr_2fr_auto]"
          onSubmit={handleSubmit(async (values) => {
            setError(null);
            try {
              await create.mutateAsync({
                name: values.name,
                description: values.description === '' ? undefined : values.description,
                primaryLocale: values.primaryLocale,
                availableLocales: values.availableLocalesCsv
                  .split(',')
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0),
                peripherals: values.hasScale ? ['scale'] : [],
                applicationVariant:
                  values.applicationVariant === '' ? null : values.applicationVariant,
              });
              reset();
            } catch (err) {
              setError(extractError(err, t));
            }
          })}
        >
          <div className="space-y-1">
            <Label>{t('terminals.field.name', 'Name')}</Label>
            <Input {...register('name')} />
            {errors.name?.message !== undefined && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label>{t('terminals.field.description', 'Description')}</Label>
            <Input {...register('description')} />
          </div>
          <div className="space-y-1">
            <Label>{t('terminals.field.primaryLocale', 'Primary locale')}</Label>
            <Input className="font-mono" {...register('primaryLocale')} />
            {errors.primaryLocale?.message !== undefined && (
              <p className="text-xs text-destructive">{errors.primaryLocale.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label>
              {t('terminals.profiles.availableLocalesCsv', 'Available locales (comma-sep)')}
            </Label>
            <Input
              className="font-mono"
              placeholder="lt, en"
              {...register('availableLocalesCsv')}
            />
            {errors.availableLocalesCsv?.message !== undefined && (
              <p className="text-xs text-destructive">{errors.availableLocalesCsv.message}</p>
            )}
          </div>
          <div className="flex items-end gap-3">
            <label className="flex h-10 items-center gap-2 whitespace-nowrap text-sm">
              <input type="checkbox" className="h-4 w-4 accent-primary" {...register('hasScale')} />
              {t('terminals.profiles.produceScale', 'Produce scale')}
            </label>
            <label className="flex h-10 items-center gap-2 whitespace-nowrap text-sm">
              {t('terminals.profiles.application', 'Application')}
              <select
                className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                aria-label={t('terminals.profiles.applicationVariantAria', 'Application variant')}
                {...register('applicationVariant')}
              >
                <option value="">{t('terminals.profiles.kindDefault', 'Kind default')}</option>
                <option value="kitchen_station">
                  {t('terminals.profiles.kitchenStation', 'Kitchen station (attendant)')}
                </option>
                <option value="food_self_order">
                  {t('terminals.profiles.foodSelfOrder', 'Food self-order (sco)')}
                </option>
              </select>
            </label>
            <Button type="submit" disabled={create.isPending}>
              {t('terminals.profiles.add', 'Add')}
            </Button>
          </div>
        </form>
        {error !== null && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </section>

      <section className="rounded-md border bg-card">
        <Table>
          <THead>
            <TR>
              <TH>{t('terminals.field.name', 'Name')}</TH>
              <TH>{t('terminals.field.primaryLocale', 'Primary locale')}</TH>
              <TH>{t('terminals.profiles.availableLocales', 'Available locales')}</TH>
              <TH>{t('terminals.profiles.peripherals', 'Peripherals')}</TH>
              <TH>{t('terminals.profiles.application', 'Application')}</TH>
              <TH>{t('terminals.profiles.countryPack', 'Country pack')}</TH>
              <TH>{t('terminals.field.description', 'Description')}</TH>
              <TH>{t('terminals.profiles.configuration', 'Configuration')}</TH>
            </TR>
          </THead>
          <TBody>
            {profiles.isLoading && (
              <TR>
                <TD colSpan={8} className="py-6 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {profiles.data?.length === 0 && (
              <TR>
                <TD colSpan={8} className="py-6 text-center text-muted-foreground">
                  {t('terminals.profiles.empty', 'No profiles yet. Add one above.')}
                </TD>
              </TR>
            )}
            {profiles.data?.flatMap((p) => [
              <TR key={p.id}>
                <TD className="font-medium">{p.name}</TD>
                <TD className="font-mono text-xs">{p.primaryLocale}</TD>
                <TD className="font-mono text-xs">{p.availableLocales.join(', ')}</TD>
                <TD className="font-mono text-xs">
                  {p.peripherals.length === 0 ? '—' : p.peripherals.join(', ')}
                </TD>
                <TD className="font-mono text-xs">{p.applicationVariant ?? '—'}</TD>
                <TD>
                  <CountryPackPicker
                    profile={p}
                    enabledPacks={enabledPacks.data ?? []}
                    allPacks={allPacks.data ?? []}
                    onError={setError}
                  />
                </TD>
                <TD className="text-sm text-muted-foreground">{p.description ?? '—'}</TD>
                <TD>
                  <div className="space-y-2">
                    <ProfileConfigEditor profile={p} />
                    <ProfilePeripheralEditor profile={p} />
                    <PinnedParametersToggle
                      target={{ scope: 'profile', profileId: p.id, profileName: p.name }}
                      open={parametersOpenFor === p.id}
                      onToggle={() => setParametersOpenFor((open) => (open === p.id ? null : p.id))}
                    />
                  </div>
                </TD>
              </TR>,
              ...(parametersOpenFor === p.id
                ? [
                    <TR key={`${p.id}-parameters`}>
                      <TD colSpan={8} className="bg-background p-5">
                        <ParameterEditor
                          target={{ scope: 'profile', profileId: p.id, profileName: p.name }}
                        />
                      </TD>
                    </TR>,
                  ]
                : []),
            ])}
          </TBody>
        </Table>
      </section>
    </div>
  );
}

function CountryPackPicker({
  profile,
  enabledPacks,
  allPacks,
  onError,
}: {
  profile: TerminalProfile;
  enabledPacks: CountryPack[];
  allPacks: CountryPack[];
  onError: (msg: string | null) => void;
}) {
  const t = useT('admin_ui');
  const bind = useBindProfileCountryPack();
  const unbind = useUnbindProfileCountryPack();
  // The bound pack might be disabled (admin disabled it after binding;
  // BOS rejects disable-with-bindings, but a missing-from-bundle row
  // can still appear here). Fall back to allPacks for the display
  // label; the dropdown options are always enabled+bundled.
  const current =
    profile.countryPackId === null
      ? null
      : (allPacks.find((p) => p.id === profile.countryPackId) ?? null);

  return (
    <div className="flex items-center gap-2">
      <Select
        className="w-44"
        value={current?.packId ?? ''}
        disabled={bind.isPending || unbind.isPending}
        onChange={(e) => {
          onError(null);
          const next = e.target.value;
          if (next === '') {
            unbind.mutateAsync(profile.id).catch((err: unknown) => onError(extractError(err, t)));
            return;
          }
          if (next === current?.packId) return;
          bind
            .mutateAsync({ profileId: profile.id, packId: next })
            .catch((err: unknown) => onError(extractError(err, t)));
        }}
      >
        <option value="">—</option>
        {enabledPacks.map((p) => (
          <option key={p.id} value={p.packId}>
            {p.displayName} ({p.country} · {p.version})
          </option>
        ))}
        {/* Edge case: the currently-bound pack is no longer in the
            enabled list (e.g. an admin-disable would have failed if
            this profile was bound, but a missing-from-bundle row can
            still hit this branch). Show the bound pack as a stale
            option so it doesn't disappear from the picker. */}
        {current !== null && enabledPacks.find((p) => p.id === current.id) === undefined && (
          <option value={current.packId} disabled>
            {current.displayName} ({current.country} · {t('terminals.profiles.stale', 'stale')})
          </option>
        )}
      </Select>
    </div>
  );
}

function extractError(err: unknown, t: Translate): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined;
    return data?.message ?? err.message;
  }
  return t('terminals.error.unexpected', 'Unexpected error');
}
